import { action, makeObservable, observable, runInAction } from 'mobx';
import { INotification } from 'stores/networkInterfaces';
import { getTimestamp } from 'utils/dateUtils';
import { navigateToOffering, navigateToOrder } from 'utils/navigateUtils';
import { safeRunRequest } from 'utils/networkRequests';

export interface INotificationQuery {
  offset?: number;
  limit?: number;
  filter?: string;

  [key: string]: unknown;
}

class NotificationStore {
  directMessages: INotification[] = [];
  watchMessages: INotification[] = [];

  totalDirectCount = 0;
  totalWatchCount = 0;

  unreadDirectCount = 0;
  unreadWatchCount = 0;

  lastOrderChange?: string = undefined;
  lastTradeChange?: string = undefined;

  constructor() {
    makeObservable(this, {
      directMessages: observable,
      watchMessages: observable,

      totalDirectCount: observable,
      totalWatchCount: observable,

      unreadDirectCount: observable,
      unreadWatchCount: observable,

      lastOrderChange: observable,
      updateLastOrderChange: action,

      lastTradeChange: observable,
      updateLastTradeChange: action,

      listDirectNotifications: action,
      listWatchNotifications: action,

      markAllDirectAsRead: action,
      markAllWatchAsRead: action,

      markDirectAsRead: action,
      markWatchAsRead: action,
      markAsRead: action
    });
  }

  formatData = (data: { attributes: INotification }[]) =>
    data?.map((item: { attributes: INotification }) => item?.attributes) as INotification[];

  updateLastOrderChange(orderId?: string) {
    this.lastOrderChange = orderId;
  }

  updateLastTradeChange(tradeId?: string) {
    this.lastTradeChange = tradeId;
  }

  async listDirectNotifications({ offset = 0, limit = 10, filter }: INotificationQuery) {
    const response = await safeRunRequest('listNotifications', { offset, limit, filter });

    runInAction(() => {
      this.totalDirectCount = response.total || 0;
      this.unreadDirectCount = response.unread || 0;
      const items = response?.data ? this.formatData(response.data) : [];

      this.directMessages = offset ? this.directMessages.concat(items) : items;
    });

    return {
      total: this.totalDirectCount,
      unread: this.unreadDirectCount,
      messages: this.directMessages
    };
  }

  async listWatchNotifications({ offset = 0, limit = 10, filter }: INotificationQuery) {
    const response = await safeRunRequest('listWatchNotifications', { offset, limit, filter });

    runInAction(() => {
      this.totalWatchCount = response.total || 0;
      this.unreadWatchCount = response.unread || 0;
      const items = response?.data ? this.formatData(response.data) : [];

      this.watchMessages = offset ? this.watchMessages.concat(items) : items;
    });

    return {
      total: this.totalWatchCount,
      unread: this.unreadWatchCount,
      messages: this.watchMessages
    };
  }

  markDirectAsRead = (notification: INotification) => {
    if (!notification.read_at) {
      this.markAsRead(notification);

      runInAction(() => {
        this.directMessages = this.directMessages.map((n) => n);
        this.unreadDirectCount = this.unreadDirectCount - 1;
      });
    }
  };

  markDirectAsUnread = (notification: INotification) => {
    if (!!notification.read_at) {
      this.markAsUnread(notification);

      runInAction(() => {
        this.directMessages = this.directMessages.map((n) => n);
        this.unreadDirectCount = this.unreadDirectCount + 1;
      });
    }
  };

  markWatchAsRead = (notification: INotification) => {
    if (!notification.read_at) {
      this.markAsRead(notification);

      runInAction(() => {
        this.watchMessages = this.watchMessages.map((n) => n);
        this.unreadWatchCount = this.unreadWatchCount - 1;
      });
    }
  };

  markWatchAsUnread = (notification: INotification) => {
    if (!!notification.read_at) {
      this.markAsUnread(notification);

      runInAction(() => {
        this.watchMessages = this.watchMessages.map((n) => n);
        this.unreadWatchCount = this.unreadWatchCount + 1;
      });
    }
  };

  async markAsRead(notification: INotification) {
    notification.read_at = getTimestamp(new Date());
    await safeRunRequest('readNotification', {
      id: notification.id,
      read: true
    });
  }

  async markAsUnread(notification: INotification) {
    notification.read_at = undefined;
    await safeRunRequest('readNotification', {
      id: notification.id,
      read: false
    });
  }

  async markAllDirectAsRead() {
    const read_at = getTimestamp(new Date());

    await safeRunRequest('readAllNotifications', {});

    runInAction(() => {
      this.directMessages = this.directMessages.map((n) => ({
        ...n,
        read_at: n.read_at || read_at
      }));
      this.unreadDirectCount = 0;
    });
  }

  async markAllWatchAsRead() {
    const read_at = getTimestamp(new Date());

    await safeRunRequest('readAllWatchNotifications', {});

    runInAction(() => {
      this.watchMessages = this.watchMessages.map((n) => ({
        ...n,
        read_at: n.read_at || read_at
      }));
      this.unreadWatchCount = 0;
    });
  }

  async navigateToNotification(notification: INotification) {
    if (
      notification?.offering_id &&
      notification?.order_id &&
      notification?.order_type !== undefined
    ) {
      return navigateToOrder(
        notification?.offering_id,
        notification?.order_id,
        notification?.order_type
      );
    }

    if (notification?.offering_id) {
      return navigateToOffering(notification.offering_id);
    }
  }
}

export default NotificationStore;
